import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient, useAuth, formatApiError } from "@/lib/auth";
import {
  ArrowLeft, UserPlus, Loader2, Trash2, KeyRound, Users, ShieldCheck, X, Copy,
  Phone as PhoneIcon, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const randomPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%";
  return Array.from({ length: 14 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
};

const UsersPage = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetTarget, setResetTarget] = useState(null); // { id, email, name, mobile_phone_number, sms_alerts_enabled }
  const [resetPw, setResetPw] = useState("");
  const [resetDone, setResetDone] = useState(false);   // holds the just-generated password after submit
  // Profile fields (name + email + phone + SMS alerts) editable in the same modal
  const [profileName, setProfileName] = useState("");
  const [profileEmail, setProfileEmail] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileSms, setProfileSms] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiClient.get("/admin/users");
      setUsers(data.users || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitAdd = async (e) => {
    e.preventDefault();
    if (!name || !email || password.length < 8) return toast.error("Fill all fields; password must be at least 8 characters.");
    setBusy(true);
    try {
      await apiClient.post("/admin/users", { name, email, password });
      toast.success(`${name} added as admin.`);
      setShowAdd(false); setName(""); setEmail(""); setPassword("");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (resetPw.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await apiClient.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetTarget.name}.`);
      setResetDone(true);  // keep the password visible until modal is closed
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const closeReset = () => {
    setResetTarget(null);
    setResetPw("");
    setResetDone(false);
    setProfileName("");
    setProfileEmail("");
    setProfilePhone("");
    setProfileSms(false);
  };

  // Pre-fill profile fields whenever a new target is opened
  useEffect(() => {
    if (resetTarget) {
      setProfileName(resetTarget.name || "");
      setProfileEmail(resetTarget.email || "");
      setProfilePhone(resetTarget.mobile_phone_number || "");
      setProfileSms(!!resetTarget.sms_alerts_enabled);
    }
  }, [resetTarget]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      const payload = {
        name: profileName,
        mobile_phone_number: profilePhone,
        sms_alerts_enabled: profileSms,
      };
      // Only send email if the partner actually changed it (avoids the collision check on unchanged rows)
      if (profileEmail && profileEmail.toLowerCase() !== (resetTarget.email || "").toLowerCase()) {
        payload.email = profileEmail;
      }
      const { data } = await apiClient.patch(`/admin/users/${resetTarget.id}/profile`, payload);
      toast.success("Profile saved");
      setResetTarget((prev) => ({ ...prev, ...data.user }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setProfileSaving(false); }
  };

  const removeUser = async (u) => {
    if (u.id === me?.id) return toast.error("You can't remove your own account.");
    if (!window.confirm(`Remove ${u.name} (${u.email})? They will lose all access immediately.`)) return;
    try {
      await apiClient.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} removed.`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard"));
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] p-6 md:p-10">
      <div className="max-w-4xl mx-auto">
        <Link to="/admin" className="mono-body text-[11px] text-nex-chrome hover:text-nex-gold inline-flex items-center gap-2 mb-6">
          <ArrowLeft size={14} /> Back to Console
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="mono-label mb-2">// STAFF &amp; ACCESS</div>
            <h1 className="serif-head text-3xl md:text-4xl">Team Members</h1>
            <p className="text-nex-chrome mt-2 text-sm">Manage who can sign in to the NEX Owner Console.</p>
          </div>
          <button data-testid="users-add-open" onClick={() => { setPassword(randomPassword()); setShowAdd(true); }} className="tick-btn-primary">
            <UserPlus size={16} /> Add Admin
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20"><Loader2 className="animate-spin text-nex-gold mx-auto" /></div>
        ) : (
          <div className="card-industrial corner-tick p-6 md:p-8" data-testid="users-table">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Users className="text-nex-gold" size={16} />
                <div className="mono-label">// ACTIVE STAFF</div>
              </div>
              <div className="mono-body text-[11px] text-nex-muted">{users.length} {users.length === 1 ? "member" : "members"}</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-white/10">
                    <th className="mono-label pb-3 pr-4">Name</th>
                    <th className="mono-label pb-3 pr-4">Email</th>
                    <th className="mono-label pb-3 pr-4">Role</th>
                    <th className="mono-label pb-3 pr-4">PIN</th>
                    <th className="mono-label pb-3 pr-4">Added</th>
                    <th className="pb-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="py-4 pr-4 text-nex-silver">
                        {u.name}
                        {u.id === me?.id && <span className="ml-2 mono-body text-[10px] text-nex-gold">(you)</span>}
                      </td>
                      <td className="py-4 pr-4 text-nex-chrome font-mono text-[12px]">{u.email}</td>
                      <td className="py-4 pr-4">
                        <span className="mono-label px-2 py-1 text-[10px] bg-nex-gold/15 text-nex-gold">{u.role?.toUpperCase() || "ADMIN"}</span>
                      </td>
                      <td className="py-4 pr-4">
                        {u.has_pin ? <ShieldCheck className="text-nex-gold" size={14} /> : <span className="text-nex-muted mono-body text-[10px]">—</span>}
                      </td>
                      <td className="py-4 pr-4 mono-body text-[11px] text-nex-muted">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-4 pl-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => { setResetTarget(u); setResetPw(randomPassword()); }}
                            data-testid={`user-reset-${u.id}`}
                            title="Reset password"
                            className="p-2 text-nex-chrome hover:text-nex-gold"
                          >
                            <KeyRound size={14} />
                          </button>
                          {u.id !== me?.id && (
                            <button
                              onClick={() => removeUser(u)}
                              data-testid={`user-delete-${u.id}`}
                              title="Remove admin"
                              className="p-2 text-nex-chrome hover:text-red-400"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ADD USER MODAL */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => !busy && setShowAdd(false)}>
            <form onSubmit={submitAdd} onClick={(e) => e.stopPropagation()} className="card-industrial corner-tick p-8 w-full max-w-md relative" data-testid="users-add-modal">
              <button type="button" onClick={() => !busy && setShowAdd(false)} className="absolute top-4 right-4 text-nex-muted hover:text-nex-silver"><X size={16} /></button>
              <div className="mono-label mb-2">// NEW ADMIN</div>
              <h2 className="serif-head text-2xl mb-6">Invite a team member</h2>
              <div className="space-y-4">
                <div>
                  <label className="mono-label block mb-1.5">Full name</label>
                  <input data-testid="add-name" value={name} onChange={(e) => setName(e.target.value)} className="input-industrial" placeholder="e.g. Robert Hatch" required />
                </div>
                <div>
                  <label className="mono-label block mb-1.5">Email</label>
                  <input data-testid="add-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-industrial" placeholder="rfh75@yahoo.com" required />
                </div>
                <div>
                  <label className="mono-label block mb-1.5">Temporary password</label>
                  <div className="flex gap-2">
                    <input data-testid="add-password" value={password} onChange={(e) => setPassword(e.target.value)} className="input-industrial font-mono" minLength={8} required />
                    <button type="button" onClick={() => setPassword(randomPassword())} className="tick-btn-secondary !py-2 !px-3" title="Generate new"><KeyRound size={14} /></button>
                    <button type="button" onClick={() => copyToClipboard(password)} className="tick-btn-secondary !py-2 !px-3" title="Copy"><Copy size={14} /></button>
                  </div>
                  <div className="mono-body text-[10px] text-nex-muted mt-1">Share this privately with the new admin — they can reset it themselves via Forgot Password.</div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setShowAdd(false)} disabled={busy} className="tick-btn-secondary flex-1 justify-center">Cancel</button>
                <button type="submit" disabled={busy} data-testid="add-submit" className="tick-btn-primary flex-1 justify-center disabled:opacity-50">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <><UserPlus size={16} /> Add Admin</>}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* RESET PASSWORD + PROFILE MODAL */}
        {resetTarget && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6 overflow-y-auto" onClick={() => !busy && !profileSaving && closeReset()}>
            <div onClick={(e) => e.stopPropagation()} className="card-industrial corner-tick p-8 w-full max-w-md relative my-8" data-testid="users-reset-modal">
              <button type="button" onClick={closeReset} className="absolute top-4 right-4 text-nex-muted hover:text-nex-silver"><X size={16} /></button>
              <div className="mono-label mb-2">// TEAM MEMBER PROFILE</div>
              <h2 className="serif-head text-2xl mb-2">{resetTarget.name}</h2>
              <p className="text-nex-chrome text-sm mb-6 font-mono">{resetTarget.email}</p>

              {/* PROFILE SECTION — phone + SMS toggle */}
              <form onSubmit={saveProfile} className="space-y-4 border-b border-nex-border pb-6 mb-6" data-testid="profile-form">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="mono-label block mb-1.5">Name</label>
                    <input
                      type="text"
                      value={profileName}
                      onChange={(e) => setProfileName(e.target.value)}
                      className="input-industrial"
                      data-testid="profile-name"
                    />
                  </div>
                  <div>
                    <label className="mono-label block mb-1.5">Email {me?.is_partner && <span className="text-nex-gold">· editable</span>}</label>
                    <input
                      type="email"
                      value={profileEmail}
                      onChange={(e) => setProfileEmail(e.target.value)}
                      disabled={!me?.is_partner}
                      className="input-industrial disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="profile-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="mono-label block mb-1.5 flex items-center gap-2"><PhoneIcon size={12} /> Mobile Phone Number</label>
                  <input
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                    placeholder="+15125551234"
                    className="input-industrial font-mono"
                    data-testid="profile-phone"
                  />
                  <div className="mono-body text-[10px] text-nex-muted mt-1">
                    Full E.164 format (e.g. +15125551234). Clear to remove.
                  </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="profile-sms-toggle">
                  <input
                    type="checkbox"
                    checked={profileSms}
                    onChange={(e) => setProfileSms(e.target.checked)}
                    className="mt-1 accent-nex-gold"
                    data-testid="profile-sms-checkbox"
                  />
                  <div>
                    <div className="text-nex-silver text-sm inline-flex items-center gap-2">
                      <MessageSquare size={13} /> Enable SMS Alerts
                    </div>
                    <div className="mono-body text-[10px] text-nex-muted mt-1">
                      Receives new-lead alerts, dual-approval notices, and any other admin SMS. Requires a mobile number on file.
                    </div>
                  </div>
                </label>
                <button type="submit" disabled={profileSaving} className="tick-btn-primary w-full justify-center text-xs" data-testid="profile-save">
                  {profileSaving ? <Loader2 size={13} className="animate-spin" /> : "Save profile"}
                </button>
              </form>

              {/* PASSWORD SECTION */}
              <form onSubmit={submitReset}>
                <div className="mono-label mb-2">// RESET PASSWORD</div>
                {resetDone ? (
                  <div data-testid="reset-done" className="space-y-4">
                    <div className="flex items-center gap-2 bg-nex-gold/10 border border-nex-gold/40 p-4">
                      <ShieldCheck className="text-nex-gold" size={18} />
                      <div className="mono-body text-sm text-nex-silver">Password updated. Copy it now — you won&apos;t see it again.</div>
                    </div>
                    <div>
                      <label className="mono-label block mb-1.5">New password</label>
                      <div className="flex gap-2">
                        <input readOnly value={resetPw} className="input-industrial font-mono" data-testid="reset-pw-final" />
                        <button type="button" onClick={() => copyToClipboard(resetPw)} className="tick-btn-secondary !py-2 !px-3" title="Copy"><Copy size={14} /></button>
                      </div>
                    </div>
                    <button type="button" onClick={closeReset} data-testid="reset-close" className="tick-btn-primary w-full justify-center">Done</button>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="mono-label block mb-1.5">New password</label>
                      <div className="flex gap-2">
                        <input data-testid="reset-pw" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} className="input-industrial font-mono" minLength={8} required />
                        <button type="button" onClick={() => setResetPw(randomPassword())} className="tick-btn-secondary !py-2 !px-3" title="Generate new"><KeyRound size={14} /></button>
                        <button type="button" onClick={() => copyToClipboard(resetPw)} className="tick-btn-secondary !py-2 !px-3" title="Copy"><Copy size={14} /></button>
                      </div>
                      <div className="mono-body text-[10px] text-nex-muted mt-1">Share this privately. Their previous password stops working immediately.</div>
                    </div>
                    <div className="flex gap-3 mt-6">
                      <button type="button" onClick={closeReset} disabled={busy} className="tick-btn-secondary flex-1 justify-center">Close</button>
                      <button type="submit" disabled={busy || !resetPw} data-testid="reset-submit" className="tick-btn-primary flex-1 justify-center disabled:opacity-50">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : <><KeyRound size={16} /> Reset Password</>}
                      </button>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersPage;
